import type { CustomField, CustomFieldValues } from './api'

/**
 * Проверка значений пользовательских полей перед отправкой (тикет #50).
 *
 * ## Зачем дублировать серверную проверку
 *
 * Это **удобство, а не защита**. Сервер проверяет то же самое и остаётся единственным,
 * кому можно верить: клиентскую проверку обходит любой, кто откроет консоль. Смысл здесь
 * в другом — сказать про опечатку сразу, а не после отправки формы, которую пользователь
 * успел закрыть.
 *
 * Поэтому правила обязаны совпадать с серверными. Расхождение хуже отсутствия проверки:
 * форма показывает «всё в порядке», а сервер отвечает отказом, и пользователь не понимает,
 * что именно исправлять.
 */

export interface FieldError {
  code: string
  message: string
}

/** Проверяет одно значение против объявления поля. */
export function validateValue(field: CustomField, value: unknown): FieldError | null {
  const empty = value === null || value === undefined || value === ''

  if (empty) {
    if (field.required) {
      return { code: field.code, message: `Поле «${field.label}» обязательно` }
    }
    return null
  }

  const text = String(value).trim()

  switch (field.dataType) {
    case 'number':
      // Number('') равен нулю, а Number('12abc') — NaN: пустоту отсекли выше, здесь
      // остаётся отличить число от того, что на него похоже.
      if (Number.isNaN(Number(text))) {
        return { code: field.code, message: `«${text}» — не число` }
      }
      return null

    case 'date':
      // Date принимает почти всё и молча выдаёт Invalid Date; сравнение с NaN — то, что
      // отличает настоящую дату от строки, похожей на неё.
      if (Number.isNaN(new Date(text).getTime())) {
        return { code: field.code, message: `«${text}» — не дата` }
      }
      return null

    case 'boolean':
      if (text !== 'true' && text !== 'false') {
        return { code: field.code, message: 'Ожидается «да» или «нет»' }
      }
      return null

    case 'enum':
      if (!field.options.includes(text)) {
        // Список выбора, принимающий что угодно, — это строка с лишними шагами.
        return {
          code: field.code,
          message: `«${text}» нет среди вариантов поля «${field.label}»`,
        }
      }
      return null

    case 'reference':
    case 'file':
      if (!UUID.test(text)) {
        return { code: field.code, message: 'Ожидается идентификатор' }
      }
      return null

    case 'string':
      return null
  }
}

/** Проверяет всю карточку; пустой массив — можно отправлять. */
export function validateValues(
  fields: CustomField[],
  values: CustomFieldValues,
): FieldError[] {
  const errors: FieldError[] = []

  for (const field of fields) {
    const error = validateValue(field, values[field.code])
    if (error) errors.push(error)
  }

  return errors
}

/**
 * Поля в порядке отображения.
 *
 * Сортировка на клиенте, а не полагаться на порядок из ответа: сервер его гарантирует,
 * но карточка может собирать поля из нескольких источников, и порядок тогда зависит от
 * того, какой запрос вернулся первым.
 */
export function inDisplayOrder(fields: CustomField[]): CustomField[] {
  return [...fields].sort(
    (a, b) => a.displayOrder - b.displayOrder || a.code.localeCompare(b.code),
  )
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
